import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { useUserOptionRender } from '@src/hooks';
import {
    THERAPIST_TYPE_OPTIONS,
    WAVELENGTH_OPTIONS,
    BODY_PART_OPTIONS,
    SKIN_TYPE_OPTIONS,
    MACHINE_OPTIONS,
} from '../constants';
import dayjs from 'dayjs';

const INV_ITEM_OPT = 'inv-offering-v1';

function makeInvoiceOfferingOptionValue(
    invoiceId: number,
    itemKey: string | number,
    offeringName: string
): string {
    return JSON.stringify({
        _t: INV_ITEM_OPT,
        inv: invoiceId,
        k: itemKey,
        n: offeringName,
    });
}

function parseInvoiceOfferingOptionValue(val: string): string | null {
    try {
        const o = JSON.parse(val) as {
            _t?: string;
            n?: string;
        };
        if (o?._t === INV_ITEM_OPT && typeof o.n === 'string' && o.n.trim()) {
            return o.n.trim();
        }
    } catch {
        /* not JSON */
    }
    return null;
}

export const useTreatmentsFormFields = (patientId?: string | number) => {
    const userOptionRender = useUserOptionRender({
        allowedUserTypes: ['doctor', 'therapist'],
        showUserType: true,
        size: 'small',
    });

    const { data: invoicesData, isLoading: invoicesLoading } = useList({
        resource: 'clinic-invoices',
        filters: patientId
            ? [
                  {
                      field: 'patient_id',
                      operator: 'eq',
                      value: patientId,
                  },
              ]
            : [],
        pagination: { mode: 'off' },
        queryOptions: {
            enabled: !!patientId,
        },
    });

    const invoicePurchasedOfferingOptions = useMemo(() => {
        const invoices = (invoicesData?.data || []) as any[];
        const out: { label: string; value: string }[] = [];

        for (const inv of invoices) {
            const items =
                inv.clinic_invoice_items || inv.clinicInvoiceItems || [];
            const invId = inv.id;
            const dateStr = inv.created_at
                ? dayjs(inv.created_at).format('DD MMM YYYY')
                : '';

            items.forEach((item: any, idx: number) => {
                const name =
                    item?.offering?.name || item?.item_name || '';
                const trimmed = String(name).trim();
                if (!trimmed) return;

                const qty = item?.quantity ?? item?.qty;
                const qtyPart =
                    qty !== undefined && qty !== null && qty !== ''
                        ? ` · Qty ${qty}`
                        : '';
                const label = `${trimmed} — Invoice #${invId}${dateStr ? ` · ${dateStr}` : ''}${qtyPart}`;

                out.push({
                    label,
                    value: makeInvoiceOfferingOptionValue(
                        invId,
                        item?.id ?? idx,
                        trimmed
                    ),
                });
            });
        }

        return out;
    }, [invoicesData?.data]);

    return useMemo(
        () => [

            {
                name: 'patient_id',
                label: 'Patient ID',
                type: 'text',
                required: false,
                defaultValue: patientId,
                layout: { span: 0 },
                style: { display: 'none' },
                hidden: true,
            },

            // Row 1: Doctor (Left) | Therapist (Right)
            {
                name: 'under_observation_doctor_user_id',
                label: 'Doctor',
                type: 'resourceSelect' as const,
                required: true,
                placeholder: 'Select doctor',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                searchSelectProps: {
                    resource: 'users',
                    optionValue: 'id',
                    searchFields: ['first_name', 'last_name'],
                    searchDebounceMs: 300,
                    pagination: { pageSize: 20 },
                    filters: [
                        {
                            field: 'status',
                            operator: 'eq',
                            value: 'active',
                        },
                        {
                            field: 'type',
                            operator: 'eq',
                            value: 'doctor',
                        },
                    ],
                    sorters: [{ field: 'first_name', order: 'asc' }],
                    optionRender: userOptionRender,
                    autoSelectFirst: true,
                    queryOptions: {
                        enabled: true,
                        staleTime: 5 * 60 * 1000,
                        cacheTime: 10 * 60 * 1000,
                        refetchOnWindowFocus: false,
                        refetchOnMount: true,
                    },
                },
            },
            {
                name: 'therapist_id',
                label: 'Therapist',
                type: 'resourceSelect' as const,
                required: true,
                placeholder: 'Select therapist',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                searchSelectProps: {
                    resource: 'users',
                    optionValue: 'id',
                    searchFields: ['first_name', 'last_name'],
                    searchDebounceMs: 300,
                    pagination: { pageSize: 20 },
                    filters: [
                        {
                            field: 'status',
                            operator: 'eq',
                            value: 'active',
                        },
                        {
                            field: 'type',
                            operator: 'eq',
                            value: 'therapist',
                        },
                    ],
                    sorters: [{ field: 'first_name', order: 'asc' }],
                    optionRender: userOptionRender,
                    autoSelectFirst: true,
                    queryOptions: {
                        enabled: true,
                        staleTime: 5 * 60 * 1000,
                        cacheTime: 10 * 60 * 1000,
                        refetchOnWindowFocus: false,
                        refetchOnMount: true,
                    },
                },
            },
            // Row 2: Therapist Type (Left) | Treatment Name (Right)
            {
                name: 'therapist_type',
                label: 'Therapist Type',
                type: 'select' as const,
                required: true,
                placeholder: 'Select therapist type',
                layout: { span: 12 },
                options: THERAPIST_TYPE_OPTIONS,
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                },
            },
            {
                name: 'treatment_name',
                label: 'Treatment Name',
                type: 'select' as const,
                required: true,
                placeholder: invoicesLoading
                    ? 'Loading purchased invoice items...'
                    : invoicePurchasedOfferingOptions.length > 0
                      ? 'Select invoice items and/or type names, Enter to add...'
                      : 'No invoice line items yet — type treatment names, Enter to add...',
                layout: { span: 12 },
                options: invoicePurchasedOfferingOptions,
                props: {
                    size: 'large',
                    mode: 'tags' as const,
                    disabled: invoicesLoading,
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                    maxTagCount: 'responsive',
                    maxTagTextLength: 24,
                },
                tooltip:
                    'Pick multiple offerings from clinic invoice line items, and/or type custom names (like Body Part).',
                onChange: (val: any, form: any) => {
                    if (!form?.setFieldsValue || !Array.isArray(val)) {
                        return;
                    }
                    const normalized = val
                        .map((item: unknown) => {
                            const s =
                                typeof item === 'string'
                                    ? item
                                    : String(item ?? '');
                            const fromInvoice =
                                parseInvoiceOfferingOptionValue(s);
                            return (fromInvoice ?? s).trim();
                        })
                        .filter(Boolean);
                    const seen = new Set<string>();
                    const unique = normalized.filter(tag => {
                        if (seen.has(tag)) return false;
                        seen.add(tag);
                        return true;
                    });
                    form.setFieldsValue({ treatment_name: unique });
                },
            },
            // Row 3: Body Parts (Left) | Session Number (Right)
            {
                name: 'body_part',
                label: 'Body Part',
                type: 'select' as const,
                required: true,
                placeholder: 'Select body parts',
                layout: { span: 12 },
                options: [...BODY_PART_OPTIONS],
                props: {
                    size: 'large',
                    mode: 'tags',
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                    maxTagCount: 'responsive',
                    maxTagTextLength: 10,
                },
                tooltip: 'Select one or more body parts, or type and press Enter to add a new one.',
            },
            {
                name: 'session_number',
                label: 'Session Number',
                type: 'number' as const,
                required: true,
                placeholder: 'Enter session number',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    min: 1,
                    max: 999,
                },
            },
            // Row 4: Skin Type (Left) | Machine Name (Right)
            {
                name: 'skin_type',
                label: 'Skin Type',
                type: 'select' as const,
                required: true,
                placeholder: 'Select skin type',
                layout: { span: 12 },
                options: SKIN_TYPE_OPTIONS,
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                },
            },
            {
                name: 'machine_name',
                label: 'Machine Name',
                type: 'customAutoComplete' as const,
                required: false,
                placeholder: 'Enter machine name',
                layout: { span: 12 },
                options: [...MACHINE_OPTIONS],
                props: {
                    size: 'large',
                    maxLength: 100,
                    showCount: true,
                    style: { width: '100%' },
                    allowClear: true,
                },
            },
            // Row 5: Machine Start Reading (Left) | Machine End Reading (Right)
            {
                name: 'machine_starting_reading',
                label: 'Machine Start Reading',
                type: 'text' as const,
                required: false,
                placeholder: 'Enter start reading',
                layout: { span: 12 },
                props: {
                    size: 'large',
                },
            },
            {
                name: 'machine_ending_reading',
                label: 'Machine End Reading',
                type: 'text' as const,
                required: false,
                placeholder: 'Enter end reading',
                layout: { span: 12 },
                props: {
                    size: 'large',
                },
            },
            // Row 6: Power Duration (Left) | Wavelength (Right)
            {
                name: 'power_duration',
                label: 'Power | Frequency | PD | Mode',
                type: 'select' as const,
                required: false,
                placeholder: 'Enter power/duration details and press Enter to create tags',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    mode: 'tags',
                    showSearch: false,
                    allowClear: true,
                    maxTagCount: 10,
                    maxTagTextLength: 20,
                    tokenSeparators: [],
                    open: false,
                    dropdownStyle: { display: 'none' },
                },
                tooltip: 'Type and press Enter to create tags',
            },
            {
                name: 'wavelength',
                label: 'Wavelength',
                type: 'select' as const,
                required: false,
                placeholder: 'Select wavelength',
                layout: { span: 12 },
                options: WAVELENGTH_OPTIONS,
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                },
            },
            // Row 7: Remark (Full Width)
            {
                name: 'remark',
                label: 'Remark',
                type: 'textarea' as const,
                required: false,
                placeholder: 'Enter any remarks or notes...',
                layout: { span: 24 },
                props: {
                    rows: 4,
                    showCount: true,
                    maxLength: 100,
                },
            },
        ],
        [
            userOptionRender,
            patientId,
            invoicesLoading,
            invoicePurchasedOfferingOptions,
        ]
    );
};
